/**
 * Modal Registry
 * 
 * Central registry for all context-based modals using the ModalProvider/useModal pattern.
 * Add new modals here to make them available throughout the application.
 * 
 * Usage:
 *   import { useModal } from 'src/components/ui-core/modal';
 *   const { openModal } = useModal();
 *   const result = await openModal('inviteUser', { ...props });
 * 
 * @see docs/ModalManager.md for full documentation
 */
import {
    CadAvlAssignmentModal,
    DeleteCadAvlAssignmentModal,
    DeleteUserModal,
    EditRoleModal,
    InviteUserModal,
    RequestCommentModal,
    ReviewInviteModal,
    TransferOwnershipModal,
    UserDetailsModal,
} from 'src/components/modals';
import { CAD_AVL_MODAL_IDS } from 'src/components/modals/modal-ids';

/**
 * Modal registry mapping modal IDs to their components
 * @type {Object.<string, React.ComponentType>}
 */
const modalRegistry = {
    /**
     * Invite User Modal - Opens the invite user form
     * Used on: Team page
     * Props: none required
     */
    inviteUser: InviteUserModal,
    
    /**
     * User Details Modal - Displays detailed user information
     * Used on: Team page
     * Props: user (object), isAdmin (boolean), roleHistory (array)
     */
    userDetails: UserDetailsModal,
    
    /**
     * Delete User Modal - Confirmation dialog for removing one or more users
     * Used on: Team page
     * Props: user (object) OR users (array), onConfirm (function)
     * Supports single user deletion and bulk deletion
     */
    deleteUser: DeleteUserModal,
    
    /**
     * Review Invite Modal - Modal for reviewing and actioning invite requests
     * Used on: Requests page
     * Props: 
     *   - request (object): The invite request object to review
     *   - isAdminOrOwner (boolean): Whether current user is admin/owner (determines view type)
     *   - onApprove (function): Callback when request is approved (admin only)
     *   - onReject (function): Callback when request is rejected (admin only)
     *   - onEditRole (function): Callback when role edit is requested (admin only)
     * 
     * View types:
     *   - Admin/Owner: Shows Requester Details, Invite Details, Status, and action buttons
     *   - Viewer/Non-Admin: Shows Invite Details, Decision, Invite Status, and Close button
     */
    reviewInvite: ReviewInviteModal,
    
    /**
     * Request Comment Modal - Optional comment modal before approve/reject actions
     * Used on: Requests page (via ReviewInviteModal)
     * Props: actionType ('approve' | 'reject'), onSubmit (function), onSkip (function)
     */
    requestComment: RequestCommentModal,
    
    /**
     * Edit Role Modal - Modal for editing user role or invite request role
     * Used on: Team page (action menu), Requests page (via ReviewInviteModal)
     * Props:
     *   - user (object): User object when editing existing user
     *   - request (object): Request object when editing pending request
     *   - isRequest (boolean): Whether editing a request (vs existing user)
     *   - onUpdate (function): Callback for updating role (existing users)
     *   - onUpdateAndApprove (function): Callback for update + approve (pending requests)
     * 
     * Features:
     *   - Role dropdown selection
     *   - Jurisdiction selector (shown only for Traffic Viewer role)
     */
    editRole: EditRoleModal,

    /**
     * Transfer Ownership Modal - Transfers agency ownership to another member
     * Used on: Team page
     * Props:
     *   - owner (object): Current agency owner row data
     *   - onConfirm (function): Callback with { new_owner_email }
     */
    transferOwnership: TransferOwnershipModal,

    [CAD_AVL_MODAL_IDS.CREATE_ASSIGNMENT]: CadAvlAssignmentModal,
    [CAD_AVL_MODAL_IDS.EDIT_ASSIGNMENT]: CadAvlAssignmentModal,
    [CAD_AVL_MODAL_IDS.DELETE_ASSIGNMENT]: DeleteCadAvlAssignmentModal,
};

export default modalRegistry;
